/**
 * PageHead — a title with a hairline rule filling the rest of the row, and an
 * optional action after it.
 *
 * The student area had three spellings of this: `.sec` with a real
 * `<span class="sec__rule">` element, `.sp-section-title` drawing the rule as
 * an ::after with nowhere to put an action, and `.page-head` / `.page-title`.
 * All three resolved to `flex: 1; height: 1px; background: var(--line)`.
 *
 * `level` is the real distinction the three spellings were confusing with each
 * other: a page's own heading is larger than the headings of the sections
 * inside it, and each has its own vertical rhythm.
 *
 *   <PageHead>My Campaigns</PageHead>                      // page level
 *   <PageHead level="section" action={<Button/>}>…         // section, spaced
 *   <PageHead level="section" tight>…                      // opens a block
 */

import type { ReactNode } from 'react';
import styles from './page-head.module.css';

interface PageHeadProps {
  /**
   * `page` is the screen's own heading; `section` is a heading inside it.
   * Defaults to `page`.
   */
  level?: 'page' | 'section';
  /**
   * A section header that opens the block it belongs to, with no preceding
   * content to separate from. Ignored at page level.
   */
  tight?: boolean;
  /** Rendered after the rule — a button, or a "View all →" link. */
  action?: ReactNode;
  /** Standfirst under the heading. Page level only in practice. */
  sub?: ReactNode;
  /** Heading tag. Defaults to h1 at page level and h2 at section level. */
  as?: 'h1' | 'h2' | 'h3';
  className?: string;
  children: ReactNode;
}

export function PageHead({ level = 'page', tight, action, sub, as, className, children }: PageHeadProps) {
  const Heading = as ?? (level === 'page' ? 'h1' : 'h2');
  const row = [
    styles.head,
    level === 'section' ? (tight ? styles.tight : styles.section) : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className={row}>
        <Heading className={level === 'page' ? styles.pageTitle : styles.sectionTitle}>
          {children}
        </Heading>
        {action && <div className={styles.action}>{action}</div>}
      </div>
      {sub && <p className={styles.sub}>{sub}</p>}
    </>
  );
}
